import React from 'react';
import SignalSim from './SignalSim';
import Constellation from './Constellation';
import TrialChart from './TrialChart';
import Roi from './Roi';
import { LateChart, LeadChart } from './Charts';
import { useCountUp, useInView } from './hooks';
import {
  BLOCKS, BUILD_IDEAS, CONTACT, DEPLOY, OUTCOMES, STATUS, TRIAL, TRUST,
} from './data';

const hue = (n) => ({ '--hue': `var(--kz-h${n})` });

export function Hero() {
  return (
    <section className="kz-hero" id="top">
      <div className="kz-hero-bg" aria-hidden="true" />
      <div className="kz-container kz-hero-grid">
        <div className="kz-hero-copy">
          <p className="kz-eyebrow"><span className="kz-kanji" lang="ja">兆し</span> Kizashi · signals ML platform</p>
          <h1 className="kz-hero-title">See the problem coming. <span>Act before it costs you.</span></h1>
          <p className="kz-hero-lead">
            Your email, tickets, chat, logs, orders and invoices already carry the early signs of what is about
            to go wrong. Kizashi reads them from any source, ties them to the customers, suppliers, systems or
            teams they concern, and scores them with detectors and models you design. When a pattern holds, it
            acts, and every alert can show exactly why.
          </p>
          <div className="kz-actions">
            <a href={CONTACT} className="wx-btn wx-btn--primary wx-btn--lg"><i className="fas fa-calendar-check" aria-hidden="true" />Book a walkthrough</a>
            <a href="#example" className="wx-btn wx-btn--ghost wx-btn--lg"><i className="fas fa-play" aria-hidden="true" />Watch it work</a>
          </div>
        </div>
        <Constellation />
      </div>
      <div className="kz-container">
        <ul className="kz-outcomes" aria-label="Outcomes">
          {OUTCOMES.map((o) => (
            <li key={o.title} className="kz-outcome" style={hue(o.hue)}>
              <span className="kz-icon-tile"><i className={o.icon} aria-hidden="true" /></span>
              <b className="kz-outcome-v">{o.value}</b>
              <h2>{o.title}</h2>
              <p>{o.text}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function Example() {
  return (
    <section className="wx-section kz-section kz-glow-a" id="example">
      <div className="kz-container">
        <p className="kz-kicker">Worked example</p>
        <h2 className="kz-h2">Customer health and churn early warning, playing live.</h2>
        <p className="kz-lead">
          The first pack built on Kizashi, and one of many things it can be designed to watch. Four made-up accounts
          send email, tickets, chat, logs and orders; each item is resolved to its customer, moves the signals and the
          health score, and a trigger fires when a high score holds. Pick a customer to see why its score moved.
        </p>
        <div className="kz-stage-frame"><SignalSim /></div>
      </div>
    </section>
  );
}

export function WhyNow() {
  return (
    <section className="wx-section wx-section--alt kz-section" id="why">
      <div className="kz-container kz-split kz-split-wide">
        <div>
          <p className="kz-kicker kz-k2">Why it matters</p>
          <h2 className="kz-h2">The cost of finding out late.</h2>
          <p className="kz-p">
            Most losses announce themselves. A customer who is about to leave replies more slowly, chases a promise
            twice, sounds shorter, and orders a narrower range, weeks before the orders stop. A supplier that is about
            to fail slips deliveries first. None of it is secret: it sits in inboxes, service desks and the ERP.
          </p>
          <p className="kz-p">
            It is simply never read together. So the business finds out from the revenue report, after the decision
            was made, when the only move left is a post-mortem.
          </p>
          <ul className="kz-checks">
            <li style={hue(6)}><b>Already your data.</b> No new instrumentation; Kizashi reads the systems you run.</li>
            <li style={hue(2)}><b>Early and specific.</b> Which account, which signal, which email, not a trend line.</li>
            <li style={hue(1)}><b>Routed to an owner.</b> The alert reaches someone who can still change the outcome.</li>
          </ul>
        </div>
        <LateChart />
      </div>
    </section>
  );
}

function Stat({ to, decimals = 0, suffix = '', label, note, run, h }) {
  const v = useCountUp(to, run);
  return (
    <div className="kz-stat" style={hue(h)}>
      <dt>{label}</dt>
      <dd>{v.toFixed(decimals)}<small>{suffix}</small></dd>
      <span>{note}</span>
    </div>
  );
}

export function Evidence() {
  const [ref, inView] = useInView();
  return (
    <section className="wx-section kz-section kz-glow-b" id="evidence">
      <div className="kz-container">
        <p className="kz-kicker">Evidence, not a promise</p>
        <h2 className="kz-h2">Scored blind, against an answer key nobody had seen.</h2>
        <p className="kz-lead">
          The customer-health pack was tested on simulated worlds that speak the real Zendesk, Microsoft Graph and ERP
          APIs, full of overt churners, silent churners and decoys that look bad but stay. Who churns comes from a secret
          seed; the answer key is sealed before the run and opened only after the predictions are frozen. These are
          simulated worlds, not a customer deployment, and the numbers below are exactly what trial #5a recorded.
        </p>
        <div className="kz-evidence">
          <LeadChart />
          <dl className="kz-stats" ref={ref}>
            <Stat h={1} to={TRIAL.medianLead} suffix=" days" label="Median warning" note="before the order stop, alerted churners" run={inView} />
            <Stat h={6} to={TRIAL.p75Lead} suffix="+ days" label="A quarter got" note="of warning or more (75th percentile)" run={inView} />
            <Stat h={5} to={TRIAL.auc} decimals={3} label="ROC-AUC" note="ranking of risk, 95% CI 0.800–0.920" run={inView} />
            <Stat h={2} to={TRIAL.precision * 100} decimals={0} suffix="%" label="Alert precision" note="about 1 in 5 alerts is a churner; the open problem" run={inView} />
          </dl>
        </div>
        <p className="kz-note">
          Honest reading: ranking is strong, alerting is where the work is. Half the churners were alerted, 40% of decoys
          still alerted at some point, and silent churners are the ones missed. More than about four weeks before the last
          order, no score tested so far beats chance.
        </p>
        <Roi />
        <details className="kz-more">
          <summary>How the score improved, trial by trial</summary>
          <TrialChart />
        </details>
      </div>
    </section>
  );
}

export function Platform() {
  return (
    <section className="wx-section wx-section--alt kz-section" id="platform">
      <div className="kz-container">
        <p className="kz-kicker kz-k3">What Kizashi is</p>
        <h2 className="kz-h2">A platform for early warning, not a single model.</h2>
        <p className="kz-lead">
          Kizashi is the machinery for watching anything your business already has data about. Bring the sources, name
          the entities, define the signals, and design the detectors, scores and triggers that matter to you. Customer
          health is the first thing built with it.
        </p>
        <ol className="kz-blocks">
          {BLOCKS.map((b, i) => (
            <li className="kz-block" key={b.title} style={hue(b.hue)}>
              <span className="kz-block-n">{String(i + 1).padStart(2, '0')}</span>
              <span className="kz-icon-tile"><i className={b.icon} aria-hidden="true" /></span>
              <h3>{b.title}</h3>
              <p>{b.text}</p>
            </li>
          ))}
        </ol>
        <div className="kz-build-head">
          <h3>What you can build with it</h3>
          <p>
            Designs the architecture supports: new signal types, extractors, detectors and a score definition, added as a
            pack, over the same ingestion, triggers, actions, console, lineage and security.
            {' '}<b>These are not shipped packs.</b> Customer health is the only pack built today.
          </p>
        </div>
        <ul className="kz-domains">
          {BUILD_IDEAS.map((d) => (
            <li key={d.title} className="kz-domain" style={hue(d.hue)}>
              <span className="kz-icon-tile"><i className={d.icon} aria-hidden="true" /></span>
              <div><h4>{d.title}</h4><p>{d.text}</p></div>
            </li>
          ))}
          <li className="kz-domain kz-domain-cta" style={hue(1)}>
            <span className="kz-icon-tile"><i className="fas fa-lightbulb" aria-hidden="true" /></span>
            <div><h4>Something else you need to see coming?</h4><p><a href={CONTACT}>Talk it through with us</a> and we will say honestly whether Kizashi fits.</p></div>
          </li>
        </ul>
      </div>
    </section>
  );
}

export function Trust() {
  return (
    <section className="wx-section kz-section kz-glow-c" id="trust">
      <div className="kz-container">
        <p className="kz-kicker kz-k5">Governance</p>
        <h2 className="kz-h2">Built to be audited, because it will be.</h2>
        <p className="kz-lead">
          Kizashi is designed to be sold to other companies, so it assumes their security, privacy and compliance teams
          will look inside. The controls are part of the platform, not an add-on.
        </p>
        <ul className="kz-trust">
          {TRUST.map((t) => (
            <li key={t.title} className="kz-trust-item" style={hue(t.hue)}>
              <span className="kz-icon-tile"><i className={t.icon} aria-hidden="true" /></span>
              <h3>{t.title}</h3>
              <p>{t.text}</p>
            </li>
          ))}
        </ul>
        <p className="kz-note">Whether erasing by key destruction satisfies GDPR and CCPA for a given contract needs legal sign-off; the design says so.</p>
      </div>
    </section>
  );
}

export function Deploy() {
  return (
    <section className="wx-section wx-section--alt kz-section" id="deploy">
      <div className="kz-container">
        <p className="kz-kicker kz-k6">Deployment</p>
        <h2 className="kz-h2">Start on one server. Grow into your cluster.</h2>
        <ul className="kz-deploy">
          {DEPLOY.map((d, i) => (
            <li key={d.title} className="kz-deploy-item" style={hue(d.hue)}>
              <span className="kz-deploy-step">{i + 1}</span>
              <span className="kz-icon-tile"><i className={d.icon} aria-hidden="true" /></span>
              <h3>{d.title}</h3>
              <span className="kz-tag">{d.tag}</span>
              <p>{d.text}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function Status() {
  return (
    <section className="wx-section kz-section" id="status">
      <div className="kz-container">
        <p className="kz-kicker kz-k7">Status</p>
        <h2 className="kz-h2">What is built, and what is not yet.</h2>
        <div className="kz-status">
          {STATUS.map((col) => (
            <div key={col.state} className={`kz-status-col is-${col.state}`}>
              <h3><span className="kz-status-dot" aria-hidden="true" />{col.label}</h3>
              <ul>{col.items.map((it) => <li key={it}>{it}</li>)}</ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Cta() {
  return (
    <section className="kz-cta">
      <div className="kz-container kz-cta-inner">
        <div>
          <h2>See what your own data is already telling you.</h2>
          <p>A walkthrough of the console and the churn example, and an honest conversation about what you would want Kizashi to watch.</p>
        </div>
        <div className="kz-actions">
          <a href={CONTACT} className="wx-btn wx-btn--primary wx-btn--lg"><i className="fas fa-calendar-check" aria-hidden="true" />Book a walkthrough</a>
          <a href="#console" className="wx-btn wx-btn--ghost wx-btn--lg">Explore the console</a>
        </div>
      </div>
    </section>
  );
}
